import type { PreviewOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Badge, Icon, Skeleton } from "@sagot/ui";

interface EffectivePermissionsProps {
  preview: PreviewOut | undefined;
  isPending: boolean;
  isError: boolean;
  total: number;
  title?: string;
  subtitle?: string;
}

/** Server-computed effective permissions (`POST /v1/roles/preview-effective`), grouped by module. */
export function EffectivePermissions({ preview, isPending, isError, total, title, subtitle }: EffectivePermissionsProps) {
  const { t } = useTranslation();
  const modules = preview ? Object.entries(preview.matrix).filter(([, actions]) => Object.values(actions).some(Boolean)) : [];
  return (
    <section aria-labelledby="effective-title" aria-live="polite" aria-busy={isPending}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 id="effective-title" className="text-sm font-bold">
            {title ?? t("roles.effective.title")}
          </h3>
          <p className="mt-0.5 text-xs text-fg-subtle">{subtitle ?? t("roles.effective.subtitle")}</p>
        </div>
        {preview ? <Badge tone="brand">{t("roles.effective.count", { granted: preview.permissions.length, total })}</Badge> : null}
      </div>
      {isError ? <p className="mt-3 text-xs font-semibold text-warning">{t("roles.effective.error")}</p> : null}
      {isPending && !preview ? (
        <div className="mt-3 space-y-2" aria-hidden="true">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-3/4" />
        </div>
      ) : null}
      {preview && modules.length === 0 ? <p className="mt-3 text-sm text-fg-muted">{t("roles.effective.none")}</p> : null}
      {modules.length > 0 ? (
        <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {modules.map(([module, actions]) => (
            <li key={module} className="rounded-md border border-line bg-surface-2 px-3 py-2">
              <div className="text-xs font-bold text-fg">{t(`roles.modules.${module}`, { defaultValue: module })}</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {Object.entries(actions)
                  .filter(([, on]) => on)
                  .map(([action]) => (
                    <span key={action} className="inline-flex items-center gap-1 text-[11px] font-semibold text-success">
                      <Icon name="check" className="size-3" />
                      {t(`roles.actions.${action}`, { defaultValue: action })}
                    </span>
                  ))}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
